import * as React from 'react'
import { VendorProfileLink } from '@/components/VendorProfileLink'
import { Building, Loader2, Mail, Store } from 'lucide-react'
import { useVendorSearch } from '@/hooks/useVendors'
import { useVenueVendorChoices } from '@/hooks/useEvents'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { FormError } from '@/components/ui/form-error'
import { newVendorCompanyInputSchema, type VendorChoiceInput } from '@lib/input/vendor.input'
import { VENDOR_SERVICE_LABELS, VENDOR_SERVICE_NOUNS, type VendorService } from '@lib/enums/vendor-service.enum'
import { PRODUCT_NAME } from '@lib/constants/product'
import type { VenueVendorChoiceDto, VendorSearchResultDto } from '@lib/dto/vendor.dto'
import { possessive } from '@lib/helpers/format'

/**
 * Where the picker finds companies: every company on the platform, as a
 * venue's staff search it, or the venue's own vendors, as the planner of an
 * event there picks from them (docs/vendor-growth.spec.md).
 */
export type PickerSource = { kind: 'venue'; venueId: string } | { kind: 'event'; eventId: string; venueName: string }

/** A company as the picker lists it, whichever source found it. */
interface PickerResult {
  vendorId: string
  name: string
  services: VendorService[]
  /** What it offers, and for venue staff its history at the venue. */
  summary: string
}

function offers(services: readonly VendorService[]): string {
  const labels = services.map((s) => VENDOR_SERVICE_LABELS[s]).join(' · ')
  return labels === '' ? 'Offers nothing yet' : labels
}

/** A search result as venue staff see it: what it offers, and its history here. */
function fromSearch(result: VendorSearchResultDto): PickerResult {
  const history =
    result.eventsHere > 0 ? `${String(result.eventsHere)} past event${result.eventsHere === 1 ? '' : 's'} here` : null
  return { ...result, summary: [offers(result.services), history].filter((part) => part !== null).join(' — ') }
}

/** One of the venue's vendors as a planner sees it: what it offers, nothing of its history here. */
function fromChoice(choice: VenueVendorChoiceDto): PickerResult {
  return { ...choice, summary: offers(choice.services) }
}

/**
 * Find a company, or set a new one up, for one service at one venue
 * (docs/planner-vendors.md, decisions 15, 16, 26 and 27).
 *
 * Venue staff search every company, and see what it offers but never its
 * people. The planner of an event picks from the venue's vendors that offer
 * the service, and sets up a company new to the platform, which joins them. A
 * company that does not offer the service cannot be added for it: what it
 * offers is its own to choose. A new company is made with its owner, who is
 * invited; the API refuses one whose name or owner is already on the platform,
 * and says what to do instead.
 */
export function VendorCompanyPicker({
  source,
  service,
  idPrefix,
  addLabel = 'Add',
  isAdded,
  onChoose,
}: {
  source: PickerSource
  service: VendorService
  /** Keeps field ids unique when two pickers are on one page. */
  idPrefix: string
  addLabel?: string
  /** A result already added for this service, so it is not offered again. */
  isAdded?: (vendorId: string) => boolean
  /** Adds the company; rejects with the error to show. */
  onChoose: (input: VendorChoiceInput) => Promise<void>
}) {
  const [q, setQ] = React.useState('')
  // Both hooks always run, each switched off for the other source.
  const search = useVendorSearch(source.kind === 'venue' ? source.venueId : '', source.kind === 'venue' ? q : '')
  const choices = useVenueVendorChoices(source.kind === 'event' ? source.eventId : null, q, service)
  const [companyName, setCompanyName] = React.useState('')
  const [contactName, setContactName] = React.useState('')
  const [contactEmail, setContactEmail] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const label = VENDOR_SERVICE_NOUNS[service]

  async function choose(input: VendorChoiceInput, reset: () => void): Promise<void> {
    setError(null)
    setBusy(true)
    try {
      await onChoose(input)
      reset()
    } catch (e) {
      setError(requestErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  function addNew(): void {
    // The new-company member, not the union: a union reports only that no
    // branch matched, which says nothing about the field that is wrong.
    const parsed = newVendorCompanyInputSchema.safeParse({ companyName, contactName, contactEmail })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Enter the company, its contact and their email.')
      return
    }
    void choose({ ...parsed.data, service }, () => {
      setCompanyName('')
      setContactName('')
      setContactEmail('')
    })
  }

  function resultAction(result: PickerResult): React.ReactNode {
    if (isAdded?.(result.vendorId) === true) return <span className="text-xs text-ink-muted">Added</span>
    if (!result.services.includes(service)) {
      return <span className="text-xs text-ink-muted">Doesn’t offer {label}</span>
    }
    return (
      <Button
        size="sm"
        variant="outline"
        disabled={busy}
        onClick={() => void choose({ vendorId: result.vendorId, service }, () => setQ(''))}
      >
        {addLabel}
      </Button>
    )
  }

  /** What there is to list, or null when there is nothing to list yet. */
  function listed(): { pending: boolean; results: PickerResult[]; total: number } | null {
    if (source.kind === 'event') {
      return {
        pending: choices.isPending,
        results: (choices.data?.vendors ?? []).map(fromChoice),
        total: choices.data?.total ?? 0,
      }
    }
    if (q.trim() === '') return null
    return { pending: search.isPending, results: (search.data?.vendors ?? []).map(fromSearch), total: search.data?.total ?? 0 }
  }

  function noneFound(): string {
    if (source.kind === 'venue') return 'No company by that name is on the platform yet. Set it up below.'
    return q.trim() === ''
      ? `None of ${possessive(source.venueName)} vendors offer ${label} yet. Set up a company that’s new below, or ask ${source.venueName} to add one.`
      : `None of ${possessive(source.venueName)} vendors for ${label} match. Set up a company that’s new below, or ask ${source.venueName} to add one.`
  }

  function renderResults(): React.ReactNode {
    const list = listed()
    if (list === null) return null
    const { pending, results, total } = list
    return (
      <ul className="divide-y divide-line overflow-hidden rounded-md border border-line">
        {pending ? (
          <li className="flex items-center gap-2 px-3 py-2 text-sm text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Searching…
          </li>
        ) : results.length === 0 ? (
          <li className="px-3 py-2 text-sm text-ink-muted">{noneFound()}</li>
        ) : (
          results.map((result) => (
            <li key={result.vendorId} className="flex items-center gap-3 px-3 py-2">
              <Store className="h-4 w-4 shrink-0 text-ink-muted" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">
                  <VendorProfileLink vendorId={result.vendorId} name={result.name} />
                </span>
                <span className="block truncate text-xs text-ink-muted">{result.summary}</span>
              </span>
              {resultAction(result)}
            </li>
          ))
        )}
        {!pending && total > results.length ? (
          <li className="px-3 py-2 text-xs text-ink-muted">
            Showing {String(results.length)} of {String(total)}. Search by name to find the rest.
          </li>
        ) : null}
      </ul>
    )
  }

  return (
    <div className="space-y-3">
      <Field label={source.kind === 'venue' ? 'Search companies' : `Search ${possessive(source.venueName)} vendors`} htmlFor={`${idPrefix}-search`}>
        <Input
          id={`${idPrefix}-search`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Company name"
        />
      </Field>
      {renderResults()}

      <div className="space-y-3 border-t border-line pt-3">
        <p className="flex items-center gap-2 text-sm font-medium">
          <Building className="h-4 w-4 text-ink-muted" />
          {source.kind === 'venue' ? 'Not on the platform yet?' : `Not one of ${possessive(source.venueName)} vendors?`}
        </p>
        <p className="text-xs text-ink-muted">
          {source.kind === 'venue'
            ? 'Their contact becomes the company’s owner and is invited to it. The company chooses what else it offers.'
            : `Add a company that’s new to ${PRODUCT_NAME}. It joins ${possessive(source.venueName)} vendors, and its contact is invited to the event as its owner.`}
        </p>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Company" htmlFor={`${idPrefix}-company`}>
            <Input
              id={`${idPrefix}-company`}
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Company name"
            />
          </Field>
          <Field label="Contact name" htmlFor={`${idPrefix}-contact`}>
            <Input
              id={`${idPrefix}-contact`}
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              placeholder="Full name"
            />
          </Field>
          <Field label="Contact email" htmlFor={`${idPrefix}-email`}>
            <Input
              id={`${idPrefix}-email`}
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="name@company.com"
            />
          </Field>
        </div>
        <Button
          type="button"
          disabled={companyName.trim() === '' || contactName.trim() === '' || contactEmail.trim() === ''}
          onClick={addNew}
          pending={busy}
        >
          <Mail className="h-4 w-4" />
          Set up and invite
        </Button>
      </div>
      {error ? <FormError>{error}</FormError> : null}
    </div>
  )
}
